import React from 'react';
import usePage from '@dashboard/hooks/usePage';
import clsx from 'clsx';
import { EllipsisVertical, GripVertical } from 'lucide-react';
import { Button as ShadcnButton, buttonVariants } from '@dashboard/components/ui/button';
import getTicketPriceHeadline, {
  getTicketFeeNote,
} from '@dashboard/services/event/getTicketPriceHeadline';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@dashboard/components/ui/accordion';
import TicketTypeEdit from './TicketTypeEdit';
import { Label } from '@dashboard/components/Form';
import { useTicketGroupEdit } from '../TicketGroupEditContext';
import useTicketTypeHelpers from '../useTicketTypeHelpers';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import TicketTypeSaleDate from '@/dashboard/scenes/PageOverview/components/TicketTypeSaleDate';
import { useTicketGroups } from '../TicketGroupsContext';
import TicketTypeBadges from './TicketTypeBadges';
import MoveTicketTypeDialog from './MoveTicketTypeDialog';
import { TOOLTIP_DELAY_MS } from '@dashboard/components/ui/tooltip';

function checkCanDeleteTicketType(ticketType, page) {
  const hasSoldTickets = ticketType.quantitySold > 0;
  if (hasSoldTickets) {
    return {
      canDeleteTicketType: false,
      canDeleteTicketTypeMessage: "You can't delete this ticket because it has made sales.",
    };
  }

  const isLastTicketTypeInPage =
    page.ticketGroups
      .flatMap(tg => tg.ticketTypes)
      .filter(ticketType => ticketType.status !== 'archived').length === 1;

  if (isLastTicketTypeInPage) {
    return {
      canDeleteTicketType: false,
      canDeleteTicketTypeMessage: 'Add a new ticket before deleting this one.',
    };
  }

  return {
    canDeleteTicketType: true,
    canDeleteTicketTypeMessage: '',
  };
}

export default function TicketTypeItem({
  dragHandleProps,
  ticketType,
  ticketGroupId,
  onSuccess,
  isDragging = false,
}) {
  const { page } = usePage();
  const { isAddingTicketType } = useTicketGroupEdit();
  const {
    onDeleteTicketType,
    onEnableDisableTicketType,
    onDuplicateTicketType,
    onMoveTicketType,
    handleMoveTicketType,
    isMoveDialogOpen,
    setIsMoveDialogOpen,
    availableGroups,
    isMoving,
    canMoveTicketType,
  } = useTicketTypeHelpers(ticketType._id);

  const { state: ticketGroups } = useTicketGroups();
  const ticketGroup = ticketGroups?.find(tg => tg._id === ticketGroupId);
  const feeNote = getTicketFeeNote({
    ticketType,
    currency: page.eventDetails?.currency,
    feeDisplay: ticketGroup?.feeDisplay,
  });

  const { canDeleteTicketType, canDeleteTicketTypeMessage } = checkCanDeleteTicketType(
    ticketType,
    page,
  );

  return (
    <AccordionItem
      value={ticketType._id}
      className={clsx('border-b-0', {
        'bg-white dark:bg-background': isDragging,
      })}
    >
      <div className="sticky top-0 bg-white dark:bg-background z-10 flex flex-row items-center gap-2 px-1">
        <div
          {...(!isAddingTicketType && dragHandleProps)}
          className={clsx(
            buttonVariants({ variant: 'ghost', size: 'icon' }),
            'cursor-grab active:cursor-grabbing',
            {
              '!cursor-not-allowed': isAddingTicketType,
            },
          )}
        >
          <GripVertical className="size-4" />
        </div>

        <div className="flex-1 min-w-0">
          <AccordionTrigger
            data-ai-focus={`ticketType:${ticketType.name}`}
            className={clsx('py-3 items-center min-w-0 hover:no-underline', {
              'opacity-50 pointer-events-none': ticketType.status === 'archived',
              'opacity-50': ticketType.status === 'disabled',
            })}
          >
            <div className="flex flex-col items-start justify-start gap-y-1 min-w-0 flex-1">
              <div className="text-sm font-medium truncate max-w-full">{ticketType?.name}</div>
              <div className="flex items-center gap-x-1.5 flex-wrap text-xs text-gray-500 dark:text-muted-foreground">
                <TicketTypeBadges
                  page={page}
                  ticketType={ticketType}
                  ticketGroup={ticketGroups?.find(tg => tg._id === ticketGroupId)}
                  allTicketTypes={ticketGroups?.flatMap(tg => tg.ticketTypes) || []}
                  allTicketGroups={ticketGroups || []}
                />
                <span>
                  {ticketType?.capacity !== null
                    ? `${ticketType.capacity - (ticketType.quantitySold || 0)} remaining`
                    : 'Unlimited'}
                  {' • '}
                  {ticketType?.quantitySold || '0'} sold
                </span>
              </div>
              {ticketType.availabilityStatus === 'available' && ticketType.status === 'enabled' && (
                <TicketTypeSaleDate
                  className="text-xs"
                  page={page}
                  ticketType={ticketType}
                  ticketGroup={ticketGroups?.find(tg => tg._id === ticketGroupId)}
                  allTicketTypes={ticketGroups?.flatMap(tg => tg.ticketTypes) || []}
                  allTicketGroups={ticketGroups || []}
                />
              )}
            </div>

            <div className="flex flex-col items-end flex-shrink-0 max-w-[40%] text-right">
              <div className="text-sm">
                {getTicketPriceHeadline({
                  ticketType,
                  currency: page.eventDetails?.currency,
                  feeDisplay: ticketGroup?.feeDisplay,
                })}
              </div>
              {feeNote && (
                <div className="text-xs text-gray-500 dark:text-muted-foreground">{feeNote}</div>
              )}
            </div>
          </AccordionTrigger>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={ticketType.status === 'archived'}>
            <ShadcnButton
              variant="ghost"
              size="icon"
              aria-label="More options"
              disabled={ticketType.status === 'archived'}
            >
              <EllipsisVertical className="size-4" />
            </ShadcnButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={onDuplicateTicketType}>Duplicate</DropdownMenuItem>

            {canMoveTicketType(ticketType) ? (
              <DropdownMenuItem onSelect={onMoveTicketType}>Move</DropdownMenuItem>
            ) : (
              <RadixTooltip.Provider delayDuration={TOOLTIP_DELAY_MS}>
                <RadixTooltip.Root>
                  <RadixTooltip.Trigger asChild>
                    <DropdownMenuItem
                      disabled
                      onSelect={e => e.preventDefault()}
                      className="opacity-50"
                    >
                      Move
                    </DropdownMenuItem>
                  </RadixTooltip.Trigger>

                  <RadixTooltip.Content
                    className={clsx(
                      'data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade select-none leading-none will-change-[transform,opacity]',
                      'inline-flex items-center rounded px-1.5 py-1.5 mx-2',
                      'bg-black dark:bg-gray-800 shadow z-50',
                    )}
                    side="left"
                  >
                    <RadixTooltip.Arrow className="fill-current text-black dark:text-gray-800 mx-auto" />
                    <span className="block text-sm text-white dark:text-gray-100 max-w-xs text-left">
                      You cannot move a ticket if it has sales or there’s only one group.
                    </span>
                  </RadixTooltip.Content>
                </RadixTooltip.Root>
              </RadixTooltip.Provider>
            )}

            <DropdownMenuItem onSelect={onEnableDisableTicketType}>
              {ticketType.status === 'enabled' ? 'Disable' : 'Enable'}
            </DropdownMenuItem>

            {canDeleteTicketType ? (
              <DropdownMenuItem onSelect={onDeleteTicketType} variant="destructive">
                Delete
              </DropdownMenuItem>
            ) : (
              <RadixTooltip.Provider delayDuration={TOOLTIP_DELAY_MS}>
                <RadixTooltip.Root>
                  <RadixTooltip.Trigger asChild>
                    <DropdownMenuItem
                      disabled
                      onSelect={e => e.preventDefault()}
                      className="opacity-50"
                    >
                      Delete
                    </DropdownMenuItem>
                  </RadixTooltip.Trigger>

                  <RadixTooltip.Content
                    className={clsx(
                      'data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade select-none leading-none will-change-[transform,opacity]',
                      'inline-flex items-center rounded px-1.5 py-1.5 mx-2',
                      'bg-black dark:bg-gray-800 shadow z-50',
                    )}
                    side="left"
                  >
                    <RadixTooltip.Arrow className="fill-current text-black dark:text-gray-800 mx-auto" />
                    <span className="block text-sm text-white dark:text-gray-100 max-w-xs text-left">
                      {canDeleteTicketTypeMessage}
                    </span>
                  </RadixTooltip.Content>
                </RadixTooltip.Root>
              </RadixTooltip.Provider>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <AccordionContent
        className={clsx('pb-4 pt-2 relative', {
          'opacity-50 pointer-events-none': ticketType.status === 'disabled',
        })}
      >
        <Label>Ticket</Label>
        <TicketTypeEdit
          ticketType={ticketType}
          ticketGroupId={ticketGroupId}
          onSuccess={onSuccess}
        />
      </AccordionContent>

      <MoveTicketTypeDialog
        isOpen={isMoveDialogOpen}
        onClose={() => setIsMoveDialogOpen(false)}
        onMove={handleMoveTicketType}
        ticketType={ticketType}
        availableGroups={availableGroups}
        isLoading={isMoving}
      />
    </AccordionItem>
  );
}
